import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLab } from '../i18n/LabContext';
import { useLang } from '../i18n/LanguageContext';

const EMPTY_FORM = { lab_id: '', name: '', duration_minutes: '', price: '',
  type: 'small', first_purchase_only: false, max_client_avg_hours: '', min_client_avg_hours: '',
  is_recommended: false, active_from_hour: '', active_to_hour: '', is_night: false };

const EMPTY_SCHED = { room_id: '', slot_name: '', from_hour: '', to_hour: '', hourly_rate: '' };

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export default function Packages() {
  const { t } = useLang();
  const { labId } = useLab();
  const selectedLab = labId;

  const [packages, setPackages] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [labInfo, setLabInfo] = useState(null); // { price_per_hour, deep_discount_warning_percent }
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [selRoomIds, setSelRoomIds] = useState([]);
  const [deposits, setDeposits] = useState({});
  const [depositSaving, setDepositSaving] = useState({});
  const [depositMsg, setDepositMsg] = useState({});

  // Vaqtga qarab narx (price schedules)
  const [schedules, setSchedules] = useState([]);
  const [schedForm, setSchedForm] = useState({ ...EMPTY_SCHED });
  const [schedEditId, setSchedEditId] = useState(null);
  const [showSchedForm, setShowSchedForm] = useState(false);
  const [schedMsg, setSchedMsg] = useState('');

  // Discount + simulation state
  const [simResult, setSimResult] = useState(null);  // { delta, client_count } | null
  const [simLoading, setSimLoading] = useState(false);
  const debouncedPrice = useDebounce(form.price, 600);
  const debouncedDur   = useDebounce(form.duration_minutes, 600);

  useEffect(() => {
    if (labId) setForm(f => ({ ...f, lab_id: labId }));
  }, [labId]);

  useEffect(() => {
    if (!selectedLab) return;
    api.get(`/labs/${selectedLab}`).then(r => {
      setPackages(r.data.packages || []);
      setLabInfo({ price_per_hour: r.data.price_per_hour, deep_discount_warning_percent: r.data.deep_discount_warning_percent ?? 30 });
    });
    api.get(`/owner/computers/layout?lab_id=${selectedLab}`)
      .then(r => {
        const roomList = r.data.rooms || [];
        setRooms(roomList);
        const init = {};
        roomList.forEach(rm => { init[rm.id] = rm.booking_deposit != null ? String(rm.booking_deposit) : '0'; });
        setDeposits(init);
      })
      .catch(() => setRooms([]));
    api.get(`/owner/price-schedules?lab_id=${selectedLab}`)
      .then(r => setSchedules(r.data || []))
      .catch(() => setSchedules([]));
  }, [selectedLab]);

  // Live simulation fetch
  useEffect(() => {
    if (!showAdd || !selectedLab || !debouncedPrice || !debouncedDur) { setSimResult(null); return; }
    const pr  = parseInt(debouncedPrice);
    const dur = parseInt(debouncedDur);
    if (!pr || !dur) { setSimResult(null); return; }
    setSimLoading(true);
    api.get(`/owner/packages/simulate?lab_id=${selectedLab}&price=${pr}&duration_minutes=${dur}`)
      .then(r => setSimResult(r.data))
      .catch(() => setSimResult(null))
      .finally(() => setSimLoading(false));
  }, [showAdd, selectedLab, debouncedPrice, debouncedDur]);

  const toggleRoom = (id) =>
    setSelRoomIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const refreshPackages = () =>
    api.get(`/labs/${selectedLab}`).then(r => setPackages(r.data.packages || []));

  const addPackage = async (e) => {
    e.preventDefault();
    if (selRoomIds.length === 0) { alert(t('minOneRoom')); return; }
    const payload = {
      name:                 form.name,
      duration_minutes:     parseInt(form.duration_minutes),
      price:                parseInt(form.price),
      room_ids:             selRoomIds,
      type:                 form.type,
      first_purchase_only:  form.first_purchase_only,
      max_client_avg_hours: form.max_client_avg_hours ? parseFloat(form.max_client_avg_hours) : null,
      min_client_avg_hours: form.min_client_avg_hours ? parseFloat(form.min_client_avg_hours) : null,
      is_recommended:       form.is_recommended,
      active_from_hour:     form.active_from_hour !== '' ? parseInt(form.active_from_hour) : null,
      active_to_hour:       form.active_to_hour   !== '' ? parseInt(form.active_to_hour)   : null,
      is_night:             form.is_night,
    };
    try {
      if (editingId) {
        await api.put(`/owner/packages/${editingId}`, payload);
      } else {
        await api.post('/owner/packages', { ...payload, lab_id: selectedLab });
      }
      closeModal();
      refreshPackages();
    } catch (err) {
      alert(err.response?.data?.error || t('error'));
    }
  };

  const closeModal = () => {
    setShowAdd(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM, lab_id: selectedLab });
    setSelRoomIds([]);
    setSimResult(null);
  };

  const startEdit = (pkg) => {
    setEditingId(pkg.id);
    setForm({
      lab_id:               pkg.lab_id,
      name:                 pkg.name,
      duration_minutes:     String(pkg.duration_minutes),
      price:                String(pkg.price),
      type:                 pkg.type || 'small',
      first_purchase_only:  !!pkg.first_purchase_only,
      max_client_avg_hours: pkg.max_client_avg_hours != null ? String(pkg.max_client_avg_hours) : '',
      min_client_avg_hours: pkg.min_client_avg_hours != null ? String(pkg.min_client_avg_hours) : '',
      is_recommended:       !!pkg.is_recommended,
      active_from_hour:     pkg.active_from_hour != null ? String(pkg.active_from_hour) : '',
      active_to_hour:       pkg.active_to_hour   != null ? String(pkg.active_to_hour)   : '',
      is_night:             !!pkg.is_night,
    });
    setSelRoomIds(pkg.room_ids || (pkg.room_id ? [pkg.room_id] : []));
    setSimResult(null);
    setShowAdd(true);
  };

  const deletePackage = async (pkg) => {
    if (!window.confirm(`"${pkg.name}" ${t('confirmDelete')}`)) return;
    try {
      const r = await api.delete(`/owner/packages/${pkg.id}`);
      if (r.data.archived) alert(`Paket arxivga o'tkazildi (${r.data.reason})`);
      refreshPackages();
    } catch (err) {
      alert(err.response?.data?.error || t('error'));
    }
  };

  const refreshSchedules = () =>
    api.get(`/owner/price-schedules?lab_id=${selectedLab}`).then(r => setSchedules(r.data || []));

  const saveSchedule = async (e) => {
    e.preventDefault();
    const payload = {
      lab_id:      selectedLab,
      room_id:     schedForm.room_id ? parseInt(schedForm.room_id) : null,
      slot_name:   schedForm.slot_name,
      from_hour:   parseInt(schedForm.from_hour),
      to_hour:     parseInt(schedForm.to_hour),
      hourly_rate: parseFloat(schedForm.hourly_rate),
    };
    try {
      if (schedEditId) {
        await api.put(`/owner/price-schedules/${schedEditId}`, payload);
      } else {
        await api.post('/owner/price-schedules', payload);
      }
      setShowSchedForm(false);
      setSchedEditId(null);
      setSchedForm({ ...EMPTY_SCHED });
      setSchedMsg('✓ Saqlandi');
      setTimeout(() => setSchedMsg(''), 2000);
      refreshSchedules();
    } catch (err) {
      alert(err.response?.data?.error || 'Xato');
    }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm('Slotni o\'chirasizmi?')) return;
    try {
      await api.delete(`/owner/price-schedules/${id}`);
      refreshSchedules();
    } catch (err) {
      alert(err.response?.data?.error || 'Xato');
    }
  };

  const startEditSchedule = (s) => {
    setSchedEditId(s.id);
    setSchedForm({
      room_id:     s.room_id ? String(s.room_id) : '',
      slot_name:   s.slot_name,
      from_hour:   String(s.from_hour),
      to_hour:     String(s.to_hour),
      hourly_rate: String(s.hourly_rate),
    });
    setShowSchedForm(true);
  };

  const saveDeposit = async (roomId) => {
    setDepositSaving(p => ({ ...p, [roomId]: true }));
    try {
      await api.put(`/owner/rooms/${roomId}`, { booking_deposit: parseFloat(deposits[roomId]) || 0 });
      setDepositMsg(p => ({ ...p, [roomId]: t('savedCheck') }));
      setTimeout(() => setDepositMsg(p => ({ ...p, [roomId]: '' })), 2000);
      setRooms(prev => prev.map(r => r.id === roomId ? { ...r, booking_deposit: parseFloat(deposits[roomId]) || 0 } : r));
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    } finally {
      setDepositSaving(p => ({ ...p, [roomId]: false }));
    }
  };

  const formatDuration = (minutes) => {
    if (minutes >= 1440) return `${minutes / 1440} kun`;
    if (minutes >= 60)   return `${minutes / 60} soat`;
    return `${minutes} daqiqa`;
  };

  const getRoomNames = (pkg) => {
    const ids = pkg.room_ids || (pkg.room_id ? [pkg.room_id] : []);
    return ids.map(id => rooms.find(r => r.id === id)?.name).filter(Boolean);
  };

  // Discount calculation
  const discountInfo = useCallback(() => {
    if (!labInfo?.price_per_hour) return null;
    const pr  = parseInt(form.price);
    const dur = parseInt(form.duration_minutes);
    if (!pr || !dur) return null;
    const effectiveHourly = pr / (dur / 60);
    const base            = parseFloat(labInfo.price_per_hour);
    if (!base) return null;
    const pct = ((base - effectiveHourly) / base) * 100;
    return { pct: Math.round(pct * 10) / 10, warn: pct >= (labInfo.deep_discount_warning_percent ?? 30) };
  }, [form.price, form.duration_minutes, labInfo]);

  const disc = discountInfo();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>{t('packagesPageTitle')}</h1>
          <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>{t('packagesSubtitle')}</div>
        </div>
        <button onClick={() => { setShowAdd(true); setSelRoomIds([]); }} style={{
          background: 'linear-gradient(135deg, var(--purple), #5b21b6)', border: 'none',
          color: 'white', padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer'
        }}>{t('addPackageBtn')}</button>
      </div>

      {/* ZALOG SOZLAMALARI */}
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 28 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>💰 {t('bookingDepositByRoom')}</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 18 }}>{t('bookingDepositByRoomDesc')}</div>
        {rooms.length === 0 ? (
          <div style={{ color: 'var(--text3)', fontSize: 13 }}>{t('noRooms')}</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {rooms.map(room => (
              <div key={room.id} style={{ background: 'var(--bg2)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                    🚪 {room.name}
                    {room.booking_deposit > 0 && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--green)' }}>{Number(room.booking_deposit).toLocaleString()} so'm</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="number" min="0" step="1000"
                      value={deposits[room.id] ?? '0'}
                      onChange={e => setDeposits(p => ({ ...p, [room.id]: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && saveDeposit(room.id)}
                      style={{ width: 110, background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)', padding: '6px 10px', borderRadius: 7, fontSize: 13, outline: 'none' }} />
                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>{t('currency')}</span>
                    <button onClick={() => saveDeposit(room.id)} disabled={depositSaving[room.id]} style={{
                      background: 'var(--purple)', border: 'none', color: '#fff', padding: '6px 12px',
                      borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: depositSaving[room.id] ? 'not-allowed' : 'pointer', opacity: depositSaving[room.id] ? 0.7 : 1,
                    }}>{t('save')}</button>
                    {depositMsg[room.id] && <span style={{ fontSize: 12, color: 'var(--green)' }}>{depositMsg[room.id]}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* VAQTGA QARAB NARX */}
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>⏰ Vaqtga qarab narx</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Har xona uchun max 3 ta vaqt sloti. Sessiya boshlanishida avtomatik qo'llanadi.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {schedMsg && <span style={{ fontSize: 12, color: 'var(--green)' }}>{schedMsg}</span>}
            <button onClick={() => { setSchedForm({ ...EMPTY_SCHED }); setSchedEditId(null); setShowSchedForm(v => !v); }}
              style={{ background: 'var(--purple)', border: 'none', color: '#fff', padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {showSchedForm ? '✕ Yopish' : '+ Slot qo\'shish'}
            </button>
          </div>
        </div>

        {/* Slot qo'shish/tahrirlash formasi */}
        {showSchedForm && (
          <form onSubmit={saveSchedule} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text2)', display: 'block', marginBottom: 4 }}>Xona (ixtiyoriy)</label>
              <select value={schedForm.room_id} onChange={e => setSchedForm(f => ({ ...f, room_id: e.target.value }))}
                style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: 7, fontSize: 12, outline: 'none' }}>
                <option value=''>Barcha xonalar</option>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text2)', display: 'block', marginBottom: 4 }}>Slot nomi</label>
              <input required placeholder="Kecha" value={schedForm.slot_name} onChange={e => setSchedForm(f => ({ ...f, slot_name: e.target.value }))}
                style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: 7, fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text2)', display: 'block', marginBottom: 4 }}>Dan (soat)</label>
              <select required value={schedForm.from_hour} onChange={e => setSchedForm(f => ({ ...f, from_hour: e.target.value }))}
                style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)', color: schedForm.from_hour !== '' ? 'var(--text)' : 'var(--text3)', padding: '8px 10px', borderRadius: 7, fontSize: 12, outline: 'none', boxSizing: 'border-box' }}>
                <option value="">-- soat --</option>
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text2)', display: 'block', marginBottom: 4 }}>Gacha (soat)</label>
              <select required value={schedForm.to_hour} onChange={e => setSchedForm(f => ({ ...f, to_hour: e.target.value }))}
                style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)', color: schedForm.to_hour !== '' ? 'var(--text)' : 'var(--text3)', padding: '8px 10px', borderRadius: 7, fontSize: 12, outline: 'none', boxSizing: 'border-box' }}>
                <option value="">-- soat --</option>
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text2)', display: 'block', marginBottom: 4 }}>Soatlik narx (so'm)</label>
              <input required type="number" min="0" step="500" placeholder="12000" value={schedForm.hourly_rate} onChange={e => setSchedForm(f => ({ ...f, hourly_rate: e.target.value }))}
                style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', borderRadius: 7, fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <button type="submit" style={{ background: 'linear-gradient(135deg, var(--purple), #5b21b6)', border: 'none', color: '#fff', padding: '9px 16px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {schedEditId ? '✓ Yangilash' : '+ Qo\'shish'}
            </button>
          </form>
        )}

        {/* Slotlar ro'yxati */}
        {schedules.length === 0 ? (
          <div style={{ color: 'var(--text3)', fontSize: 13, marginTop: 16 }}>Hali slot qo'shilmagan</div>
        ) : (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {schedules.map(s => {
              const fmtH = h => String(h).padStart(2, '0') + ':00';
              const isNight = s.from_hour > s.to_hour;
              return (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px' }}>
                  <span style={{ fontSize: 11, background: 'rgba(139,92,246,0.15)', color: '#a78bfa', border: '1px solid #a78bfa', borderRadius: 5, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                    🚪 {s.room_name || 'Barcha xonalar'}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', minWidth: 80 }}>{s.slot_name}</span>
                  <span style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                    {fmtH(s.from_hour)} → {fmtH(s.to_hour)}
                    {isNight && <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--text3)' }}>(kecha)</span>}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--cyan)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                    {Number(s.hourly_rate).toLocaleString()} so'm/soat
                  </span>
                  <button onClick={() => startEditSchedule(s)} style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)', color: 'var(--cyan)', padding: '5px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>✏️</button>
                  <button onClick={() => deleteSchedule(s.id)} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', color: '#ef4444', padding: '5px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>🗑</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* PAKETLAR RO'YHATI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
        {packages.map(pkg => {
          const roomNames = getRoomNames(pkg);
          return (
            <div key={pkg.id} style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: 'var(--purple)', filter: 'blur(30px)', opacity: 0.2 }} />
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: 10,
                  background: pkg.type === 'big' ? 'rgba(139,92,246,0.2)' : pkg.type === 'premium' ? 'rgba(245,158,11,0.15)' : 'rgba(0,212,255,0.1)',
                  color: pkg.type === 'big' ? '#a78bfa' : pkg.type === 'premium' ? '#f59e0b' : 'var(--cyan)',
                  border: `1px solid ${pkg.type === 'big' ? '#a78bfa' : pkg.type === 'premium' ? '#f59e0b' : 'var(--cyan)'}`,
                  borderRadius: 5, padding: '2px 7px',
                }}>
                  {pkg.type === 'big' ? t('pkgTypeBig') : pkg.type === 'premium' ? 'Premium' : t('pkgTypeSmall')}
                </span>
                {pkg.is_recommended && <span style={{ fontSize: 10, background: 'rgba(250,204,21,0.15)', color: '#fbbf24', border: '1px solid #fbbf24', borderRadius: 5, padding: '2px 7px' }}>⭐ Tavsiya</span>}
                {pkg.is_night && <span style={{ fontSize: 10, background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid #818cf8', borderRadius: 5, padding: '2px 7px' }}>🌙 Tungi</span>}
                {pkg.first_purchase_only && <span style={{ fontSize: 10, background: 'rgba(52,211,153,0.15)', color: '#34d399', border: '1px solid #34d399', borderRadius: 5, padding: '2px 7px' }}>Yangi</span>}
                {pkg.active_from_hour != null && pkg.active_to_hour != null && (
                  <span style={{ fontSize: 10, background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid #818cf8', borderRadius: 5, padding: '2px 7px' }}>
                    🌙 {String(pkg.active_from_hour).padStart(2,'0')}:00–{String(pkg.active_to_hour).padStart(2,'0')}:00
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>{formatDuration(pkg.duration_minutes)}</div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{pkg.name}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                {roomNames.length > 0 ? roomNames.map(name => (
                  <span key={name} style={{ fontSize: 11, color: 'var(--cyan)', background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.25)', borderRadius: 6, padding: '2px 8px' }}>📍 {name}</span>
                )) : <span style={{ fontSize: 11, color: 'var(--text3)' }}>{t('roomNotSet')}</span>}
              </div>
              <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 24, color: 'var(--cyan)', marginTop: 12 }}>
                {Number(pkg.price).toLocaleString()}<span style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'Inter, sans-serif' }}> {t('currency')}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                {Math.round(pkg.price / (pkg.duration_minutes / 60)).toLocaleString()} {t('perHour')}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 14, position: 'relative', zIndex: 2 }}>
                <button onClick={() => startEdit(pkg)} style={{ flex: 1, background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)', color: 'var(--cyan)', padding: '7px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✏️ {t('editPkg')}</button>
                <button onClick={() => deletePackage(pkg)} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', color: '#ef4444', padding: '7px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>🗑</button>
              </div>
            </div>
          );
        })}
        {packages.length === 0 && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', color: 'var(--text3)', padding: 40 }}>{t('noPackagesList')}</div>
        )}
      </div>

      {/* MODAL */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: 20 }}>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 32, width: 440, maxWidth: '100%', position: 'relative' }}>
            <h3 style={{ marginBottom: 20 }}>{editingId ? t('editPackageTitle') : t('newPackageTitle')}</h3>
            <form onSubmit={addPackage}>

              {/* Nom */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>{t('packageNameLabel')}</label>
                <input type="text" required placeholder="1 Soat" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  style={{ width: '100%', background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>

              {/* Davomiylik */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>{t('durationLabel')}</label>
                <input type="number" required placeholder="60" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })}
                  style={{ width: '100%', background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>

              {/* Narx */}
              <div style={{ marginBottom: 6 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>{t('priceLabel')}</label>
                <input type="number" required placeholder="20000" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })}
                  style={{ width: '100%', background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>

              {/* Discount badge */}
              {disc !== null && (
                <div style={{ marginBottom: 14 }}>
                  <span style={{ fontSize: 12, color: disc.pct > 0 ? (disc.warn ? '#fbbf24' : 'var(--green)') : '#ef4444' }}>
                    {t('pkgDiscountLabel')}: {disc.pct > 0 ? `-${disc.pct}%` : `+${Math.abs(disc.pct)}%`}
                  </span>
                </div>
              )}

              {/* Chegirma ogohlantirish */}
              {disc?.warn && (
                <div style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid #fbbf24', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                  <span style={{ fontSize: 12, color: '#fbbf24' }}>
                    ⚠️ {t('pkgDiscountWarn').replace('{pct}', disc.pct)}
                  </span>
                </div>
              )}

              {/* Paket turi */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>{t('pkgTypeLabel')}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['small', 'big', 'premium'].map(tp => (
                    <button key={tp} type="button" onClick={() => setForm(f => ({ ...f, type: tp }))} style={{
                      flex: 1, padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: `1.5px solid ${form.type === tp ? 'var(--cyan)' : 'var(--border)'}`,
                      background: form.type === tp ? 'rgba(0,212,255,0.1)' : 'var(--card2)',
                      color: form.type === tp ? 'var(--cyan)' : 'var(--text2)',
                    }}>
                      {tp === 'small' ? t('pkgTypeSmall') : tp === 'big' ? t('pkgTypeBig') : 'Premium'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Big paket xususiyatlari */}
              {form.type === 'big' && (
                <>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.first_purchase_only} onChange={e => setForm(f => ({ ...f, first_purchase_only: e.target.checked }))} />
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>{t('pkgFirstPurchaseOnly')}</span>
                  </label>

                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>{t('pkgMaxAvgHours')}</label>
                    <input type="number" min="0" step="0.5" placeholder="0" value={form.max_client_avg_hours}
                      onChange={e => setForm(f => ({ ...f, max_client_avg_hours: e.target.value }))}
                      style={{ width: '100%', background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.is_recommended} onChange={e => setForm(f => ({ ...f, is_recommended: e.target.checked }))} />
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>⭐ {t('pkgIsRecommended')}</span>
                  </label>
                </>
              )}

              {/* Premium paket xususiyatlari */}
              {form.type === 'premium' && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Min oylik soat (ko'rish uchun)</label>
                  <input type="number" min="0" step="0.5" placeholder="0" value={form.min_client_avg_hours}
                    onChange={e => setForm(f => ({ ...f, min_client_avg_hours: e.target.value }))}
                    style={{ width: '100%', background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              )}

              {/* Tungi paket */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_night} onChange={e => setForm(f => ({ ...f, is_night: e.target.checked }))} />
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>🌙 Tungi paket (tungi bo'limda ko'rsatiladi)</span>
              </label>

              {/* Vaqt oralig'i */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>🌙 Vaqt oralig'i (ixtiyoriy)</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {[['active_from_hour', 'dan'], ['active_to_hour', 'gacha']].map(([field, hint]) => (
                    <select key={field} value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                      style={{ flex: 1, background: 'var(--card2)', border: '1px solid var(--border)', color: form[field] !== '' ? 'var(--text)' : 'var(--text3)', padding: '8px 12px', borderRadius: 8, fontSize: 13, outline: 'none' }}>
                      <option value="">-- {hint} --</option>
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Tanlanmasa — har doim ko'rinadi. Toshkent vaqti (UTC+5).</div>
              </div>

              {/* Xona tanlash */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 8 }}>
                  {t('roomSelectLabel')} <span style={{ color: '#ef4444' }}>*</span>
                </label>
                {rooms.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>{t('noRoomsFound')}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {rooms.map(room => {
                      const checked = selRoomIds.includes(room.id);
                      return (
                        <label key={room.id} onClick={() => toggleRoom(room.id)} style={{
                          display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                          padding: '10px 12px', borderRadius: 8,
                          background: checked ? 'rgba(0,212,255,0.1)' : 'var(--card2)',
                          border: `1.5px solid ${checked ? 'var(--cyan)' : 'var(--border)'}`,
                          transition: 'all 0.15s',
                        }}>
                          <div style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0, border: `2px solid ${checked ? 'var(--cyan)' : 'var(--border)'}`, background: checked ? 'var(--cyan)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {checked && <span style={{ color: '#000', fontSize: 12, fontWeight: 800 }}>✓</span>}
                          </div>
                          <span style={{ fontSize: 13, color: checked ? 'var(--cyan)' : 'var(--text)', fontWeight: checked ? 600 : 400 }}>{room.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {selRoomIds.length === 0 && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 6 }}>{t('roomSelectRequired')}</div>}
              </div>

              {/* Simulyatsiya */}
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>📊 {t('pkgSimTitle')}</div>
                {!labInfo?.price_per_hour ? (
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t('pkgSimNoRate')}</div>
                ) : simLoading ? (
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t('pkgSimLoading')}</div>
                ) : simResult ? (
                  <>
                    <div style={{ fontSize: 12, color: simResult.delta < 0 ? '#ef4444' : 'var(--green)' }}>
                      {t('pkgSimResult')
                        .replace('{count}', simResult.client_count)
                        .replace('{sign}', simResult.delta >= 0 ? '+' : '')
                        .replace('{amt}', Math.abs(simResult.delta).toLocaleString())}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>{t('pkgSimNote')}</div>
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Narx va davomiylikni kiriting</div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={closeModal} style={{ flex: 1, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', padding: 10, borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>{t('cancelShort')}</button>
                <button type="submit" disabled={selRoomIds.length === 0} style={{
                  flex: 1, background: selRoomIds.length > 0 ? 'linear-gradient(135deg, var(--purple), #5b21b6)' : 'var(--card2)',
                  border: 'none', color: selRoomIds.length > 0 ? 'white' : 'var(--text3)',
                  padding: 10, borderRadius: 8, cursor: selRoomIds.length > 0 ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600,
                }}>{editingId ? t('update') : t('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
